import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import { isErrorInfo } from '../../../shared/interfaces/error-info.interface';
import { TicketStatsPeriod } from '../../../shared/models/ticket-stats.model';
import { TicketStatsUiModel } from '../../../shared/ui-models/ticket-stats.ui-model';
import { getTicketStats } from '../../../shared/services/data/ticket.data';
import { PRIORITIES } from '../../../shared/mappers/ticket-stats.mapper';
import { APP_ROUTES } from '../../../shared/config/routes-config';
import { RouteConfig } from '../../../shared/interfaces/routes-config.interface';
import { LoadingData } from '../../../ui/LoadingData';
import {
  describeLoadError,
  LoadError
} from '../../../shared/components/core-ui/load-error/load-error.component';
import { CHIP, PRIORITY_STYLE } from '../../shared/ticket-visuals';

// Brand palette (tailwind.config.js): primary green for what comes in, coral for what goes
// out; the lighter shade of each is the handoff part.
const COLOR = { new: '#237c58', in: '#9ac3b2', closed: '#f4603d', out: '#fab6a6', grid: '#eaeaea' };
const CARD = 'rounded-2xl border border-gray-200 bg-white p-5 shadow-md';
const PERIODS: { key: TicketStatsPeriod; label: string }[] = [
  { key: 'day', label: 'Days' },
  { key: 'week', label: 'Weeks' },
  { key: 'month', label: 'Months' }
];

const Metric: React.FC<{ label: string; value: string | number; sub?: string }> = ({
  label,
  value,
  sub
}) => (
  <div className={CARD}>
    <div className="text-xs font-gilroy-medium uppercase tracking-wide text-neutral-300">
      {label}
    </div>
    <div className="mt-1 font-gilroy-bold text-3xl text-neutral-500">{value}</div>
    {sub ? <div className="mt-1 text-xs text-neutral-400">{sub}</div> : null}
  </div>
);

const queuePath = () => (APP_ROUTES.TICKETS_MODULE.CHILDREN as RouteConfig).QUEUE.FULL_PATH;

/**
 * Ticket performance for one team (Trello #244): what came in and went out, how fast, what
 * is open now, and the work done. Counts only, from server.integrator.helpdesk, which also
 * decides the scope: staff see their own row, team leads and admins see everyone.
 */
export const TicketsPerformanceContainer: React.FC = () => {
  const [period, setPeriod] = useState<TicketStatsPeriod>('week');
  const [team, setTeam] = useState<number | undefined>();
  const [stats, setStats] = useState<TicketStatsUiModel | undefined>();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | undefined>();

  const load = useCallback(() => {
    setLoading(true);
    return getTicketStats(period, team).then((result) => {
      if (isErrorInfo(result)) {
        setLoadError(
          result.status === 403
            ? 'You are not in any team on the helpdesk yet, so there is nothing to show.'
            : result.status === 503
            ? 'Ticket reporting is not switched on yet.'
            : describeLoadError(result)
        );
      } else {
        setLoadError(undefined);
        setStats(result);
      }
      setLoading(false);
    });
  }, [period, team]);

  useEffect(() => {
    load();
  }, [load]);

  const maxCategory = Math.max(1, ...(stats?.categories ?? []).map((c) => c.n));

  return (
    <div className="flex h-full flex-col gap-5 md:min-h-[calc(100vh-45px)] md:bg-[#f5f7fc] md:px-[2rem] md:py-[3rem]">
      <section className="flex flex-col gap-3">
        <Link to={queuePath()} className="text-sm text-primary-700">
          ← Tickets
        </Link>
        <div className="flex flex-wrap items-center gap-3">
          <span className="h-8 w-1.5 rounded-full bg-secondary-500" aria-hidden="true" />
          <h1 className="font-gilroy-bold text-2xl text-neutral-500">Ticket performance</h1>
          {stats ? (
            <span className="text-sm text-neutral-400">
              {stats.team.name} · {stats.windowLabel} · Cairo time
            </span>
          ) : null}
          <span className="ml-auto flex flex-wrap items-center gap-2">
            {stats && stats.teams.length > 1 ? (
              <select
                className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-neutral-500"
                value={stats.team.id}
                onChange={(event) => setTeam(Number(event.target.value))}
              >
                {stats.teams.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            ) : null}
            <span className="flex rounded-lg border border-gray-200 bg-white p-0.5" role="tablist">
              {PERIODS.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  role="tab"
                  aria-selected={p.key === period}
                  className={`rounded-md px-3 py-1.5 text-sm font-gilroy-medium ${
                    p.key === period ? 'bg-primary-50 text-primary-700' : 'text-neutral-400'
                  }`}
                  onClick={() => setPeriod(p.key)}
                >
                  {p.label}
                </button>
              ))}
            </span>
          </span>
        </div>
      </section>

      {loading && !stats ? (
        <LoadingData />
      ) : loadError ? (
        <LoadError message={loadError} onRetry={load} />
      ) : stats ? (
        <>
          <section className="grid grid-cols-2 gap-4 lg:grid-cols-3 xl:grid-cols-6">
            <Metric
              label="New"
              value={stats.totals.newTickets}
              sub={`${stats.totals.newByEmail} by email · ${stats.totals.handedIn} handed in`}
            />
            <Metric
              label="Closed"
              value={stats.totals.closed}
              sub={`${stats.totals.handedOut} handed on`}
            />
            <Metric label="Open now" value={stats.openNow} />
            <Metric
              label="First response"
              value={stats.firstResponse}
              sub={`median · ${stats.awaitingResponse} still waiting`}
            />
            <Metric label="Time to close" value={stats.timeToClose} sub="median" />
            <Metric label="Emergencies" value={stats.emergencies} sub="among new tickets" />
          </section>

          <section className={CARD}>
            <h2 className="mb-1 font-gilroy-bold text-neutral-500">Tickets in and out</h2>
            <p className="mb-3 text-xs text-neutral-400">
              In: new tickets that started in this team, plus tickets handed in. Out: tickets closed
              here, plus tickets handed on to the next team.
            </p>
            <div style={{ height: 280 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats.points} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
                  <CartesianGrid vertical={false} stroke={COLOR.grid} />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="newTickets" name="New" stackId="in" fill={COLOR.new} />
                  <Bar dataKey="handedIn" name="Handed in" stackId="in" fill={COLOR.in} />
                  <Bar dataKey="closed" name="Closed" stackId="out" fill={COLOR.closed} />
                  <Bar dataKey="handedOut" name="Handed on" stackId="out" fill={COLOR.out} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </section>

          <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
            <section className={CARD}>
              <h2 className="mb-3 font-gilroy-bold text-neutral-500">What came in</h2>
              {stats.categories.length === 0 ? (
                <p className="text-sm text-neutral-300">No new tickets in this period.</p>
              ) : (
                <ul className="flex flex-col gap-2">
                  {stats.categories.slice(0, 10).map((c) => (
                    <li key={c.label} className="text-sm">
                      <div className="flex justify-between gap-3 text-neutral-500">
                        <span className="truncate" title={c.label}>
                          {c.label}
                        </span>
                        <span className="font-gilroy-medium">{c.n}</span>
                      </div>
                      <div className="mt-1 h-1.5 rounded-full bg-creams-300">
                        <div
                          className="h-1.5 rounded-full bg-primary-500"
                          style={{ width: `${(c.n / maxCategory) * 100}%` }}
                        />
                      </div>
                    </li>
                  ))}
                </ul>
              )}
              <div className="mt-4 flex flex-wrap gap-1.5">
                {stats.priorities.map((p) => (
                  <span key={p.key} className={`${CHIP} ${PRIORITY_STYLE[p.key as 'P1']}`}>
                    {p.key} · {p.n}
                  </span>
                ))}
                <span className={`${CHIP} bg-blue text-neutral-500`}>Email · {stats.byEmail}</span>
                <span className={`${CHIP} bg-creams-500 text-creams-900`}>
                  Raised in admin · {stats.byAdmin}
                </span>
                {stats.segments.map((s) => (
                  <span key={s.label} className={`${CHIP} bg-creams-200 text-neutral-500`}>
                    {s.label} · {s.n}
                  </span>
                ))}
              </div>
            </section>

            <section className={CARD}>
              <h2 className="mb-3 font-gilroy-bold text-neutral-500">Open now, by age</h2>
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="text-neutral-400">
                    <th className="py-2 font-gilroy-medium">Waiting</th>
                    {PRIORITIES.map((p) => (
                      <th key={p} className="py-2 text-right font-gilroy-medium">
                        {p}
                      </th>
                    ))}
                    <th className="py-2 text-right font-gilroy-medium">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.backlog.map((row) => (
                    <tr key={row.age} className="border-t border-gray-100 text-neutral-500">
                      <td className="py-2">{row.age}</td>
                      {PRIORITIES.map((p) => (
                        <td key={p} className="py-2 text-right">
                          {row.byPriority[p] || '·'}
                        </td>
                      ))}
                      <td className="py-2 text-right font-gilroy-medium">{row.total}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          </div>

          <section className={CARD}>
            <h2 className="mb-1 font-gilroy-bold text-neutral-500">Work done</h2>
            <p className="mb-3 text-xs text-neutral-400">
              {stats.showsEveryone
                ? 'Everyone who works this team’s queue, in this period.'
                : 'Your own work in this period. Team leads see everyone in the team.'}
            </p>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px] text-left text-sm">
                <thead>
                  <tr className="bg-creams-200 text-neutral-500">
                    {['Person', 'Closed', 'Notes', 'Guest replies', 'Handed on', 'Taken'].map(
                      (h) => (
                        <th key={h} className="px-3 py-2 font-gilroy-bold">
                          {h}
                        </th>
                      )
                    )}
                  </tr>
                </thead>
                <tbody>
                  {stats.people.map((p) => (
                    <tr key={p.user_id} className="border-t border-gray-100 text-neutral-500">
                      <td className="px-3 py-2 font-gilroy-medium">
                        {p.name}
                        {p.isYou ? (
                          <span className={`${CHIP} ml-2 bg-primary-50 text-primary-700`}>You</span>
                        ) : null}
                      </td>
                      <td className="px-3 py-2">{p.closed}</td>
                      <td className="px-3 py-2">{p.notes}</td>
                      <td className="px-3 py-2">{p.replies}</td>
                      <td className="px-3 py-2">{p.handoffs}</td>
                      <td className="px-3 py-2">{p.taken}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <p className="text-xs text-neutral-300">
            New counts a ticket for the team it started in. First response is the first note, reply
            or assignment by someone other than the person who raised it. Times are medians over the
            period; days, weeks (from Monday) and months are in Cairo time.
          </p>
        </>
      ) : null}
    </div>
  );
};
